// Stand-in scenes for the courses that don't have art yet.
// Everything here is procedural, so swapping in a real GLB later means
// replacing this component with a model island (see GodotIsland).
import React, { useMemo, useRef, useState } from 'react';
import { Float } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Fitted, HaloRing, PLATFORM_TOP, ShadowBlob, useCloned } from './helpers';

/** Editor-style wireframe cage — reads as "this asset is a placeholder". */
const WireCage = ({ accent, size = 3, reduced, ...props }) => {
  const geometry = useMemo(
    () => new THREE.EdgesGeometry(new THREE.BoxGeometry(size, size, size)),
    [size]
  );
  const ref = useRef();
  useFrame((state, delta) => {
    if (!reduced && ref.current) ref.current.rotation.y += delta * 0.08;
  });
  return (
    <lineSegments ref={ref} geometry={geometry} {...props}>
      <lineBasicMaterial color={accent} transparent opacity={0.28} toneMapped={false} />
    </lineSegments>
  );
};

/** Small shapes circling the core. */
const Orbiters = ({ accent, count = 7, radius = 2.4, top = PLATFORM_TOP, height = 1.9, reduced }) => {
  const group = useRef();
  const items = useMemo(
    () =>
      new Array(count).fill(0).map((_, i) => {
        const a = (i / count) * Math.PI * 2;
        return {
          key: i,
          position: [Math.cos(a) * radius, Math.sin(a * 2) * 0.5, Math.sin(a) * radius],
          scale: 0.1 + (i % 3) * 0.045,
        };
      }),
    [count, radius]
  );

  useFrame((state, delta) => {
    if (reduced || !group.current) return;
    group.current.rotation.y += delta * 0.32;
  });

  return (
    <group ref={group} position={[0, top + height, 0]}>
      {items.map(({ key, position, scale }) => (
        <mesh key={key} position={position} scale={scale}>
          <octahedronGeometry args={[1, 0]} />
          {/* Unclamped so the bloom pass picks them up as glowing motes. */}
          <meshBasicMaterial color={accent} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
};

/** Stacked "code blocks" — the Python stand-in. */
const CodeStack = ({ accent, reduced }) => {
  const group = useRef();
  useFrame(({ clock }) => {
    if (reduced || !group.current) return;
    const t = clock.getElapsedTime();
    group.current.children.forEach((child, i) => {
      child.rotation.y = t * 0.25 + i * 0.5;
    });
  });

  return (
    <group ref={group}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[0, 0.42 + i * 0.62, 0]}>
          <boxGeometry args={[1.7 - i * 0.28, 0.5, 1.7 - i * 0.28]} />
          <meshStandardMaterial
            color="#16203A"
            emissive={accent}
            emissiveIntensity={0.22}
            roughness={0.35}
            metalness={0.55}
          />
        </mesh>
      ))}
    </group>
  );
};

/** Wireframe globe with rings — the Web-3D stand-in. */
const Globe = ({ accent, reduced }) => {
  const shell = useRef();
  const rings = useRef();

  useFrame((state, delta) => {
    if (reduced) return;
    if (shell.current) shell.current.rotation.y += delta * 0.3;
    if (rings.current) {
      rings.current.rotation.z += delta * 0.22;
      rings.current.rotation.x += delta * 0.1;
    }
  });

  return (
    <group position={[0, 1.15, 0]}>
      <mesh ref={shell}>
        <icosahedronGeometry args={[1.15, 2]} />
        <meshBasicMaterial color={accent} wireframe transparent opacity={0.55} toneMapped={false} />
      </mesh>
      <mesh>
        <icosahedronGeometry args={[0.82, 3]} />
        <meshStandardMaterial
          color="#16203A"
          emissive={accent}
          emissiveIntensity={0.35}
          roughness={0.25}
          metalness={0.7}
        />
      </mesh>
      <group ref={rings}>
        <mesh rotation={[Math.PI / 2.6, 0, 0]}>
          <torusGeometry args={[1.7, 0.022, 12, 96]} />
          <meshBasicMaterial color={accent} transparent opacity={0.7} toneMapped={false} />
        </mesh>
        <mesh rotation={[Math.PI / 2, Math.PI / 3, 0]}>
          <torusGeometry args={[2.05, 0.016, 12, 96]} />
          <meshBasicMaterial color={accent} transparent opacity={0.4} toneMapped={false} />
        </mesh>
      </group>
    </group>
  );
};

const PlaceholderIsland = ({ variant, accent, accentInk, reduced }) => {
  // Same platform art as the game-dev island, so the three scenes feel like
  // one world even while two of them are stand-ins.
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  const core =
    variant === 'python' ? (
      <CodeStack accent={accent} reduced={reduced} />
    ) : (
      <Globe accent={accent} reduced={reduced} />
    );

  return (
    <group>
      <Fitted object={platform} size={6} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3.1} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={3.25} position={[0, top + 0.02, 0]} />

      <group position={[0, top, 0]}>
        {reduced ? (
          core
        ) : (
          <Float speed={1.5} rotationIntensity={0.25} floatIntensity={0.5} floatingRange={[0, 0.18]}>
            {core}
          </Float>
        )}
        <WireCage accent={accent} size={3.1} reduced={reduced} position={[0, 1.6, 0]} />
      </group>

      <Orbiters accent={accent} top={top} reduced={reduced} />
    </group>
  );
};

export default PlaceholderIsland;
